/* Sign-in by a one-time code, against the same three jackdesk_auth endpoints
 * every Jackdesk app uses. No passwords anywhere, and no second account for
 * the ERP.
 *
 * None of these calls send an Authorization header: jackdesk_auth refuses
 * sign-in to a caller presenting a valid token, which is what happens when
 * somebody else's session is still in this browser's storage.
 */
import { publishIdentity, saveSession } from '@jackdesk/shell'
import { API_BASE, AUTH_BASE } from './config'

export class AuthError extends Error {
  constructor(readonly status: number, message: string, readonly code: string | null) {
    super(message)
  }
}

async function post<T>(path: string, body: unknown): Promise<T> {
  let response: Response
  try {
    response = await fetch(`${AUTH_BASE}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
  } catch {
    throw new AuthError(0, 'Cannot reach Jackdesk from this device. Check your connection.', 'offline')
  }
  const text = await response.text()
  let parsed: Record<string, unknown> | null = null
  try { parsed = text ? JSON.parse(text) : null } catch { /* not JSON */ }
  if (!response.ok) {
    const said = parsed?.message ?? parsed?.detail ?? parsed?.error
    throw new AuthError(response.status, String(said ?? `Sign-in failed (${response.status})`),
                        typeof parsed?.code === 'string' ? parsed.code : null)
  }
  return parsed as T
}

export type OtpStatus = {
  user_exists: boolean
  has_valid_otp: boolean
  validity_remaining_minutes: number
}

export const checkEmail = (email: string) =>
  post<OtpStatus>('/auth/login/otp/status', { email })

export const requestCode = (email: string, force = false) =>
  post<{ validity_minutes?: number }>('/auth/login/otp/request',
                                      force ? { email, force: true } : { email })

/**
 * May this address use the ERP at all?
 *
 * Asked before a code is ever requested. jackdesk_auth is shared and is never
 * told which app is asking, so it would happily email a code to somebody from
 * an organisation with no ERP workspace, who would sign in and land on "your
 * organisation's workspace is not ready yet". A code somebody can do nothing
 * with is worse than a refusal: it looks like the product working.
 *
 * Being unable to reach the gateway does NOT block sign-in — auth's own gates
 * still stand, and not being able to check is not the same as being refused.
 */
export async function mayUseErp(email: string): Promise<{ allowed: boolean; reason: string }> {
  try {
    const response = await fetch(`${API_BASE}/v1/auth/may-sign-in/`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    })
    if (!response.ok) return { allowed: true, reason: 'unchecked' }
    return await response.json()
  } catch {
    return { allowed: true, reason: 'unchecked' }
  }
}

export async function verifyCode(email: string, otp: string): Promise<void> {
  const answer = await post<{ access: string; refresh: string; user: { id: number } }>(
    '/auth/login/otp/verify', { email, otp })
  saveSession({ access: answer.access, refresh: answer.refresh, user: answer.user })
  // Only on an explicit sign-in: an app that merely opened must not claim the
  // shared identity for whoever happened to be in its storage.
  publishIdentity(answer.user?.id)
}
